/**
 * Platform Admin: allow-list users and app access (separate from Trading Agent admin).
 * Route: /admin — linked from launcher header for admins.
 * Platform themed (orbs, dark) — no roles; app access is defined per user per app (default lowest role, e.g. viewer).
 */
import { useEffect, useState } from 'react';
import { adminApi, type AllowedUser } from '@/services/admin';
import { AdminAppsSection } from '@/components/admin/AdminAppsSection';
import { Button } from '@/components/ui/button';
import { PlatformBackground, PlatformOrbs, PlatformSparkles } from '@/components/platform';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/services/api';

export default function PlatformAdmin() {
  const [users, setUsers] = useState<AllowedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addEmail, setAddEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const u = await adminApi.listUsers(true);
      setUsers(u);
    } catch (e) {
      setError(extractErrorMessage(e, 'Failed to load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addEmail.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await adminApi.addUser(addEmail.trim(), []);
      setAddEmail('');
      await load();
    } catch (e) {
      setError(extractErrorMessage(e, 'Failed to add user'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (id: string) => {
    if (!confirm('Remove this user from the allow list?')) return;
    setError(null);
    try {
      await adminApi.deleteUser(id);
      await load();
    } catch (e) {
      setError(extractErrorMessage(e, 'Failed to remove user'));
    }
  };

  if (loading) {
    return (
      <div className="platform-admin-wrap relative flex min-h-full flex-col">
        <PlatformBackground variant="platform-admin" />
        <div className="platform-admin-content relative z-10 flex flex-1 flex-col items-center justify-center p-6">
          <h1 className="platform-admin-heading text-2xl font-bold">Platform Admin</h1>
          <p className="platform-admin-muted mt-2">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="platform-admin-wrap relative flex min-h-full flex-col">
      <div className="platform-admin-bg" aria-hidden>
        <PlatformOrbs />
        <PlatformSparkles />
        <div className="platform-admin-grid" />
      </div>
      <div className="platform-admin-overlay" aria-hidden />

      <div className="platform-admin-content relative z-10 overflow-y-auto p-6">
        <div className="mx-auto max-w-3xl space-y-8">
          <div>
            <h1 className="platform-admin-heading text-2xl font-bold">Platform Admin</h1>
            <p className="platform-admin-muted mt-2">
              Allow users on the platform and define which apps they can access. When you grant a
              user access to an app, they get that app’s lowest role by default (e.g. viewer for
              Trading Agent).
            </p>
          </div>

          {error && (
            <div className="rounded-lg bg-[var(--bad, #B8353E)]/15 p-4 text-sm text-[var(--bad, #B8353E)]">
              {error}
            </div>
          )}

          <section className="platform-admin-card rounded-xl border border-[color-mix(in srgb, var(--accent) 2%, transparent)] bg-[var(--surface)]/90 p-6">
            <h2 className="platform-admin-heading text-lg font-semibold mb-3">Add user</h2>
            <p className="platform-admin-muted text-sm mb-4">
              Add a user to the platform allow-list (they can log in). Define their app access
              below.
            </p>
            <form onSubmit={handleAddUser} className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium platform-admin-heading">Email</label>
                <input
                  type="email"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  placeholder="user@example.com"
                  disabled={submitting}
                  className={cn(
                    'flex h-9 w-64 rounded-md border border-[color-mix(in srgb, var(--accent) 3%, transparent)] bg-[var(--bg-elevated)] px-3 py-1 text-sm text-[var(--fg)]',
                    'placeholder:text-[var(--fg-muted)]',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-2, var(--accent))]'
                  )}
                />
              </div>
              <Button
                type="submit"
                disabled={submitting || !addEmail.trim()}
                className="btn-primary-platform"
              >
                Add user
              </Button>
            </form>
          </section>

          <section className="platform-admin-card rounded-xl border border-[color-mix(in srgb, var(--accent) 2%, transparent)] bg-[var(--surface)]/90 p-6">
            <h2 className="platform-admin-heading text-lg font-semibold mb-3">Allowed users</h2>
            {users.length === 0 ? (
              <p className="platform-admin-muted">No users yet. Add one above.</p>
            ) : (
              <ul className="divide-y divide-[color-mix(in srgb, var(--accent) 15%, transparent)] rounded-lg border border-[color-mix(in srgb, var(--accent) 2%, transparent)]">
                {users.map((u) => (
                  <li
                    key={u.id}
                    className={cn(
                      'flex items-center justify-between px-4 py-3',
                      !u.active && 'opacity-60'
                    )}
                  >
                    <div>
                      <span className="font-medium platform-admin-heading">{u.email}</span>
                      {!u.active && (
                        <span className="ml-2 text-xs platform-admin-muted">(inactive)</span>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteUser(u.id)}
                      className="text-[var(--bad, #B8353E)] hover:text-[var(--bad, #B8353E)] hover:bg-[var(--bad, #B8353E)]/10"
                    >
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="platform-admin-card rounded-xl border border-[color-mix(in srgb, var(--accent) 2%, transparent)] bg-[var(--surface)]/90 p-6">
            <h2 className="platform-admin-heading text-lg font-semibold mb-2">Define app access</h2>
            <p className="platform-admin-muted text-sm mb-4">
              Select a user and grant or revoke access to apps. When you grant access, the user is
              given that app’s lowest role by default (e.g. viewer for Trading Agent); you can
              change their per-app role here.
            </p>
            <AdminAppsSection />
          </section>
        </div>
      </div>
    </div>
  );
}
